import React, { memo, useEffect, useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { useFormContext } from 'react-hook-form';
import { Button, ButtonFill, ComponentColor, ComponentSize } from '@tcc/fds';
import { getMessage } from 'util/locale';

type WizardFooterProps = {
  testId: string;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  lastStepActions?: React.ReactNode[];
  disableSaveButton?: boolean;
};

const baseString = 'wizard-footer';

export const WizardFooter = memo(
  function WizardFooter({ testId, setActiveStep, lastStepActions, disableSaveButton }: WizardFooterProps) {
    const { trigger, clearErrors, formState: { isSubmitting, isSubmitSuccessful } } = useFormContext();
    const {
      isFirstStep,
      isLastStep,
      previousStep,
      nextStep,
      activeStep
    } = useWizard();

    useEffect(() => {
      setActiveStep(activeStep + 1);
    }, [activeStep, setActiveStep]);

    const handleBack = useCallback(() => {
      clearErrors();
      previousStep();
    }, [clearErrors, previousStep]);

    const handleNext = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
      // The form is still submitted even with type="button" on the Next button.
      if (!isLastStep) {
        e.preventDefault();
      }

      const valid = await trigger();
      if (valid) {
        nextStep();
      }
    }, [trigger, nextStep, isLastStep]);

    const showLastStepActions = isLastStep && lastStepActions && lastStepActions.length > 0;
    const isSaveDisabled = isSubmitting || isSubmitSuccessful || disableSaveButton;

    return (
      <footer className={baseString} data-test-id={`${testId}-footer`}>
        {!isFirstStep && (
          <Button
            type="button"
            fill={ButtonFill.Outline}
            color={ComponentColor.Neutral}
            onClick={handleBack}
            testId={`${testId}-back-button`}
            size={ComponentSize.Lg}
          >
            {getMessage('button-text__back')}
          </Button>
        )}
        {showLastStepActions ? lastStepActions : (
          <Button
            type={isLastStep ? 'submit' : 'button'}
            onClick={handleNext}
            testId={`${testId}-next-button`}
            size={ComponentSize.Lg}
            disabled={isSaveDisabled}
          >
            {getMessage(`button-text__${isLastStep ? 'save' : 'next'}`)}
          </Button>
        )}
      </footer>
    );
  },
);
